import type { FormDefinition, FormEntry } from '@/api/types/Forms'
import { useState } from 'react'
import { LeaveSheet } from './LeaveSheet'
import { extractEntries } from './logic/extractEntries'
import { nextVisibleStepId, prevVisibleStepId, stepProgress } from './logic/navigation'
import type {
	AddressState,
	ChoiceState,
	DateState,
	FormStates,
	StepState,
} from './logic/stepState'
import { isBirthDateStep } from './logic/visibility'
import { AddressStep } from './steps/AddressStep'
import { ChoiceStep } from './steps/ChoiceStep'
import { ConsentStep } from './steps/ConsentStep'
import { DateStep } from './steps/DateStep'
import { InfoStep } from './steps/InfoStep'
import { QuestionStep } from './steps/QuestionStep'

interface FormRunnerProps {
	definition: FormDefinition
	onSubmit: (entries: FormEntry[]) => void
	isSubmitting: boolean
	/** Called once the patient has confirmed they want to leave unfinished. */
	onExit: () => void
}

/**
 * Walks a patient through a form one step at a time and hands back the answers at the end.
 *
 * Holds every step's state so going back keeps what was entered. Steps hidden by a condition
 * are skipped in both directions, and are left out of the answers.
 */
export const FormRunner = ({
	definition,
	onSubmit,
	isSubmitting,
	onExit,
}: FormRunnerProps) => {
	const [currentStepId, setCurrentStepId] = useState(definition.steps[0]?.id ?? 1)
	const [states, setStates] = useState<FormStates>({})
	const [isLeaving, setIsLeaving] = useState(false)

	const step = definition.steps.find((s) => s.id === currentStepId)
	if (!step) {
		return null
	}

	const setStepState = (state: StepState) =>
		setStates((previous) => ({ ...previous, [step.id]: state }))

	const goNext = () => {
		if (isSubmitting) {
			return
		}
		const next = nextVisibleStepId(definition, step.id, states)
		if (next === null) {
			onSubmit(extractEntries(definition, states))
		} else {
			setCurrentStepId(next)
		}
	}

	const previous = prevVisibleStepId(definition, step.id, states)
	const goBack = previous === null ? undefined : () => setCurrentStepId(previous)

	// Nothing typed yet means nothing to lose, so the close needs no second thought.
	const hasAnswers = Object.keys(states).length > 0
	const requestClose = () => (hasAnswers ? setIsLeaving(true) : onExit())

	// Keyed on the step, so a step never inherits the last one's local state: two consecutive
	// steps of one type render the same component at the same position, and without the key
	// React would keep the groups a patient expanded, or a consent they opened, on the next step.
	const common = {
		step,
		onNext: goNext,
		onBack: goBack,
		onClose: requestClose,
		progress: stepProgress(definition, step.id),
		isSubmitting,
	}
	const state = states[step.id]

	const renderStep = () => {
		switch (step.type) {
			case 'INFO':
				return (
					<InfoStep key={step.id} {...common} state={undefined} onChange={setStepState} />
				)
			case 'QUESTION':
				return (
					<QuestionStep
						key={step.id}
						{...common}
						state={state as string}
						onChange={setStepState}
					/>
				)
			case 'CHOICE':
				return (
					<ChoiceStep
						key={step.id}
						{...common}
						state={state as ChoiceState}
						onChange={setStepState}
					/>
				)
			case 'DATE':
				return (
					<DateStep
						key={step.id}
						{...common}
						state={state as DateState}
						onChange={setStepState}
						isBirthDate={isBirthDateStep(definition, step.id)}
					/>
				)
			case 'ADDRESS':
				return (
					<AddressStep
						key={step.id}
						{...common}
						state={state as AddressState}
						onChange={setStepState}
					/>
				)
			case 'CONSENT':
				return (
					<ConsentStep
						key={step.id}
						{...common}
						state={undefined}
						onChange={setStepState}
					/>
				)
		}
	}

	return (
		<>
			{renderStep()}
			<LeaveSheet
				open={isLeaving}
				onClose={() => setIsLeaving(false)}
				onLeave={() => {
					setIsLeaving(false)
					onExit()
				}}
			/>
		</>
	)
}
